
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Client } from "@/components/programs/ClientSelection";
import { TierFormData } from "./program-creation/types";
import { useNavigate } from "react-router-dom";
import { saveProgram, createProgramActivity } from "@/services/program-service";

export function useProgramOperations() {
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSaveProgram = async (
    tierData: TierFormData,
    selectedClient: Client | null,
    setIsSaving: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    if (!selectedClient) {
      toast({
        title: "Error",
        description: "No client selected. Please select a client to create a program.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication Error",
          description: "You must be logged in to create a program",
          variant: "destructive",
        });
        setIsSaving(false);
        return;
      }

      // Get program data from local storage
      const programDataStr = localStorage.getItem('programData');
      if (!programDataStr) {
        toast({
          title: "Error",
          description: "Program data not found. Please try again.",
          variant: "destructive",
        });
        setIsSaving(false);
        return;
      }
      
      const programData = JSON.parse(programDataStr);
      
      // Get preferences data from local storage
      const preferencesDataStr = localStorage.getItem('preferencesData');
      const preferencesData = preferencesDataStr ? JSON.parse(preferencesDataStr) : {};
      
      // Save tier data to localStorage for consistency
      localStorage.setItem('tierData', JSON.stringify(tierData));

      // Save program to database
      const data = await saveProgram(
        programData,
        tierData,
        preferencesData,
        selectedClient,
        user.id
      );

      // Create activity record if program was created successfully
      if (data && data.length > 0) {
        const programId = data[0].program_id;
        const programName = programData.programName || "New Program";

        await createProgramActivity(user.id, programId, programName);
      }

      toast({
        title: "Success",
        description: "Program created successfully!",
      });
      
      // Clear local storage data
      localStorage.removeItem('programData');
      localStorage.removeItem('tierData');
      localStorage.removeItem('preferencesData');
      
      // Navigate to programs page after successful creation
      setTimeout(() => {
        navigate('/programs');
      }, 1500);
      
    } catch (error) {
      console.error("Error in handleSaveProgram:", error);
      toast({
        title: "Error",
        description: "An error occurred while creating the program",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return {
    handleSaveProgram,
  };
}
